import { CheckCircle2, XCircle } from 'lucide-react'
import type { ClinSparkStudyPackage, TabKey } from '../types'
import { BuildBlueprint, SourceQuality, type FocusRequest, type NavigateFn } from './study/BuildBlueprint'
import EligibilityTab from './study/EligibilityTab'
import LabPanelsTab from './study/LabPanelsTab'
import RangesTab from './study/RangesTab'
import { Metric, Notice, Section } from './study/StudyUi'

export { BuildBlueprint }

type Props = {
  tab: TabKey
  data: ClinSparkStudyPackage
  search: string
  onSearchChange?: (next: string) => void
  onNavigate?: NavigateFn
  focus?: FocusRequest | null
}

const noop = () => {}

function humanize(text: string) {
  return text.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

function ValidationSummary({ data }: { data: ClinSparkStudyPackage }) {
  const checks = Object.entries(data.quality_control?.checks || {})
  const contradictions = data.quality_control?.contradictions || []
  const gaps = data.build_gaps || []
  return (
    <Section title="Build Validation" description="Validation is based on object relationships and evidence coverage, not merely non-empty arrays.">
      <div className="grid gap-3 md:grid-cols-4">
        <Metric label="Extraction status" value={data.status} />
        <Metric label="Readiness" value={`${data.quality_control?.build_readiness_score ?? 0}%`} />
        <Metric label="Build gaps" value={gaps.length} tone={gaps.length ? 'amber' : 'green'} />
        <Metric label="AI required" value={data.ai_required ? 'Yes' : 'No'} />
      </div>
      <SourceQuality data={data} />
      {checks.length > 0 && (
        <div>
          <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">Checks</h4>
          <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {checks.map(([name, passed]) => (
              <li key={name} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                {passed ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" aria-hidden={true} /> : <XCircle className="h-4 w-4 shrink-0 text-rose-500" aria-hidden={true} />}
                <span>{humanize(name)}</span>
                <span className="sr-only">{passed ? 'passed' : 'failed'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {gaps.length > 0 && <Notice><div className="font-medium">Build gaps</div><ul className="mt-1 list-disc pl-5">{gaps.map((gap) => <li key={gap}>{humanize(gap)}</li>)}</ul></Notice>}
      {contradictions.length > 0 && <Notice tone="red"><div className="font-medium">Contradictions</div><ul className="mt-1 list-disc pl-5">{contradictions.map((item) => <li key={item}>{item}</li>)}</ul></Notice>}
      <details className="rounded-lg border border-gray-200 dark:border-gray-700">
        <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200">Raw validation data</summary>
        <pre className="max-h-[480px] overflow-auto border-t border-gray-200 bg-gray-50 p-4 text-xs dark:border-gray-700 dark:bg-gray-900">{JSON.stringify({ quality_control: data.quality_control, build_gaps: data.build_gaps, validation: (data as unknown as Record<string, unknown>).validation }, null, 2)}</pre>
      </details>
    </Section>
  )
}

export default function ParserResultTab({ tab, data, search, onSearchChange = noop, onNavigate, focus }: Props) {
  if (tab === 'visits') return <BuildBlueprint data={data} search={search} onSearchChange={onSearchChange === noop ? undefined : onSearchChange} onNavigate={onNavigate} />
  if (tab === 'ie') return <EligibilityTab data={data} search={search} onSearchChange={onSearchChange} onNavigate={onNavigate} focus={focus} />
  if (tab === 'ranges') return <RangesTab data={data} search={search} onSearchChange={onSearchChange} onNavigate={onNavigate} focus={focus} />
  if (tab === 'lab-panels') return <LabPanelsTab data={data} search={search} onSearchChange={onSearchChange} onNavigate={onNavigate} />
  if (tab === 'constraints') {
    return (
      <Section title="Constraint Definitions" description="Generalized constraints from the AI extraction pipeline.">
        <Notice>Constraints are produced by AI extraction, which is not part of the deterministic package. Numeric limits from the protocol are on the Ranges tab.</Notice>
      </Section>
    )
  }
  if (tab === 'validation') return <ValidationSummary data={data} />
  return null
}
